// /src/pages/EditPurity.jsx
import React, { useEffect, useMemo, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

import {
  useGetPurityByIdQuery,
  useUpdatePurityMutation,
  useActivatePurityMutation,
  useDeactivatePurityMutation,
} from "../toolkitServices/purityApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";

// ---------- Shared UI tokens ----------
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const makeSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderWidth: 1,
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: "none",
    ":hover": { borderColor: hasError ? "#ef4444" : "#9481ce" },
    borderRadius: 10,
    fontSize: "0.92rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  menu: (base) => ({ ...base, borderRadius: 10, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    backgroundColor: state.isSelected ? "#efeafd" : state.isFocused ? "#f7f6ff" : "white",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, color: "#111827" }),
});

const EMPTY = {
  category: null,
  purity: "",
  shortName: "",
  finePercentage: "",
  description: "",
};

const EditPurity = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // fetch current purity
  const {
    data: purityResp,
    isLoading: isPurityLoading,
    error: purityError,
  } = useGetPurityByIdQuery(id, { skip: !id });

  // fetch categories
  const {
    data: catResp,
    isLoading: isCatLoading,
    error: catError,
  } = useGetAllCategoriesQuery({ page: 1, limit: 200, search: "" });

  const categoryOptions = useMemo(() => {
    const list = catResp?.data || [];
    return list.map((c) => ({ label: c.name, value: c._id }));
  }, [catResp]);

  const doc = purityResp?.data;

  const {
    register,
    handleSubmit,
    control,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: EMPTY, mode: "onBlur" });

  // prefill form when doc arrives
  useEffect(() => {
    if (!doc) return;
    const catId = typeof doc.categoryId === "object" ? doc.categoryId?._id : doc.categoryId;
    const catName =
      doc.categoryName ||
      (typeof doc.categoryId === "object" ? doc.categoryId?.name : undefined);

    reset({
      category: catId ? { value: String(catId), label: catName || "Category" } : null,
      purity: doc.purity || "",
      shortName: doc.shortName || "",
      finePercentage:
        doc.finePercentage === 0 || doc.finePercentage
          ? String(doc.finePercentage)
          : "",
      description: doc.description || "",
    });

    requestAnimationFrame(() => setFocus("purity"));
  }, [doc, reset, setFocus]);

  // mutations
  const [updatePurity, { isLoading: isSaving, error: saveErr, isSuccess: saveOk }] =
    useUpdatePurityMutation();
  const [activatePurity, { isLoading: actLoading }] = useActivatePurityMutation();
  const [deactivatePurity, { isLoading: deactLoading }] = useDeactivatePurityMutation();

  // toasts + redirect on success
  useEffect(() => {
    if (saveOk) {
      toast.success("Purity updated");
      navigate("/masters/purity-master-list"); // ✅ go to list after success
    }
  }, [saveOk, navigate]);

  useEffect(() => {
    if (purityError) {
      const msg =
        purityError?.data?.msg ||
        purityError?.data?.message ||
        purityError?.error ||
        "Failed to load purity";
      toast.error(msg);
    }
  }, [purityError]);

  useEffect(() => {
    if (catError) toast.error("Failed to load categories");
  }, [catError]);

  useEffect(() => {
    if (saveErr) {
      const msg =
        saveErr?.data?.msg || saveErr?.data?.message || saveErr?.error || "Update failed";
      toast.error(msg);
    }
  }, [saveErr]);

  // save
  const onSubmit = async (form) => {
    if (!doc?._id) return;
    const payload = {
      id: doc._id,
      purity: (form.purity || "").trim(),
      shortName: (form.shortName || "").trim().toUpperCase(),
      finePercentage: (form.finePercentage || "").trim(),
      description: (form.description || "").trim(),
      categoryId: form?.category?.value,
      categoryName: form?.category?.label,
    };
    try {
      await updatePurity(payload).unwrap();
    } catch {
      /* toast in effect */
    }
  };

  // activate/deactivate (stays on page)
  const onToggleActive = useCallback(async () => {
    if (!doc?._id) return;
    try {
      if (doc.isActive) {
        await deactivatePurity(doc._id).unwrap();
        toast.success("Purity deactivated");
      } else {
        await activatePurity(doc._id).unwrap();
        toast.success("Purity activated");
      }
    } catch (e) {
      toast.error(e?.data?.msg || "Action failed");
    }
  }, [doc, activatePurity, deactivatePurity]);

  // shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
      if ((e.ctrlKey && e.key.toLowerCase() === "s") || (e.key === "Enter" && e.ctrlKey)) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, handleSubmit]);

  const busy = isSaving || actLoading || deactLoading || isSubmitting;

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/masters/purity-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Purity List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              Edit Purity
            </h1>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Enter</kbd> to save,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go to list
            </p>
          </div>

          {doc && (
            <button
              onClick={onToggleActive}
              disabled={busy}
              className={[
                "px-3 py-1.5 rounded-lg text-sm border transition",
                doc.isActive
                  ? "border-red-300 text-red-600 hover:bg-red-50"
                  : "border-green-300 text-green-700 hover:bg-green-50",
                busy ? "opacity-60 cursor-not-allowed" : "",
              ].join(" ")}
              title={doc.isActive ? "Deactivate" : "Activate"}
            >
              {doc.isActive ? "Deactivate" : "Activate"}
            </button>
          )}
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        onAnimationComplete={() => requestAnimationFrame(() => setFocus("purity"))}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {(busy || isPurityLoading) && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        {isPurityLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : !doc ? (
          <div className="p-6 text-center text-red-500">Purity not found</div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
            {/* Purity */}
            <div>
              <label className="block text-[13px] font-medium text-gray-700 mb-1">
                Purity<span className="text-red-500">*</span>
              </label>
              <input
                {...register("purity", { required: true, minLength: 1, maxLength: 50 })}
                aria-invalid={!!errors.purity}
                placeholder="e.g. 22K, 916, 750"
                autoComplete="off"
                className={`${baseInput} ${!!errors.purity ? errRing : okRing}`}
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-[13px] font-medium text-gray-700 mb-1">
                Category<span className="text-red-500">*</span>
              </label>
              <Controller
                name="category"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={categoryOptions}
                    isClearable
                    placeholder={
                      isCatLoading ? "Loading categories…" : catError ? "Failed to load" : "Select category"
                    }
                    isDisabled={isCatLoading || !!catError}
                    classNamePrefix="react-select"
                    styles={makeSelectStyles(!!errors.category)}
                  />
                )}
              />
            </div>

            {/* Short Name */}
            <div>
              <label className="block text-[13px] font-medium text-gray-700 mb-1">Short Name</label>
              <input
                {...register("shortName")}
                aria-invalid={false}
                placeholder="e.g. 22K, P916"
                autoComplete="off"
                className={`${baseInput} ${okRing}`}
              />
            </div>

            {/* Fine Percentage */}
            <div>
              <label className="block text-[13px] font-medium text-gray-700 mb-1">Fine Percentage</label>
              <input
                {...register("finePercentage", {
                  validate: (v) => !v || /^\d+(\.\d{1,2})?$/.test(v),
                })}
                aria-invalid={!!errors.finePercentage}
                placeholder="e.g. 91.6"
                autoComplete="off"
                className={`${baseInput} ${!!errors.finePercentage ? errRing : okRing}`}
              />
            </div>

            {/* Description (full width) */}
            <div className="md:col-span-2">
              <label className="block text-[13px] font-medium text-gray-700 mb-1">Description</label>
              <textarea
                {...register("description")}
                aria-invalid={false}
                rows={2}
                placeholder="Enter description"
                className={`${baseInput} ${okRing} resize-y`}
              />
            </div>

            {/* Actions */}
            <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 pt-1">
              <motion.button
                whileHover={{ scale: busy ? 1 : 1.01 }}
                whileTap={{ scale: busy ? 1 : 0.98 }}
                type="submit"
                disabled={busy}
                className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </motion.button>

              <Link
                to="/masters/purity-master-list"
                className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                title="Cancel and go back"
              >
                Cancel
              </Link>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default EditPurity;
